import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileCompletionPrompt } from '@/components/ProfileCompletionPrompt';
import { SocialMediaLinksTab } from '@/components/profile/SocialMediaLinksTab';
import { 
  Building2, 
  TrendingUp, 
  DollarSign, 
  Users, 
  Globe,
  Play,
  ExternalLink,
  Instagram,
  Twitter,
  Youtube,
  Facebook,
  ChevronUp,
  ChevronDown,
  MapPin,
  Calendar,
  Briefcase,
  UserCheck,
  Star,
  Trophy,
  Target,
  Activity,
  BarChart3,
  ArrowUpRight,
  Mail,
  Edit,
  Settings
} from 'lucide-react';
import { useBrandData } from '@/hooks/useBrandData';
import { useBrandAthletes } from '@/hooks/useBrandAthletes';
import { useUser } from '@/contexts/UserContext';
import { useUserRole } from '@/hooks/useUserRole';
import BrandProfileEditDrawer from './BrandProfileEditDrawer';
import { AthleteEngagementGrid } from './AthleteEngagementGrid';
import { BrandOrganizationTab } from './BrandOrganizationTab';
import { userService } from '@/services/userService';
import { AdminProfileSection } from '@/components/admin/AdminProfileSection';



const BrandProfile = () => {
  const navigate = useNavigate();
  const { refreshUserData } = useUser();
  const { isAdmin } = useUserRole();
  const { brandData, loading, error } = useBrandData();
  const { athletes, handleViewHistory } = useBrandAthletes();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editSection, setEditSection] = useState<'overview' | 'company' | 'marketing' | 'athletes' | 'contact'>('overview');

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="h-20 w-20 rounded-full bg-muted animate-pulse" />
              <div className="space-y-2">
                <div className="h-6 w-48 bg-muted animate-pulse rounded" />
                <div className="h-4 w-32 bg-muted animate-pulse rounded" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !brandData) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center space-y-4">
            <p className="text-muted-foreground">
              {error || 'Unable to load brand profile. Please try again.'}
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!brandData.brand) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              Brand profile data not found. Please complete your brand profile setup.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brand = brandData.brand;
  const profile = brandData;

  const handleEditClick = (section: 'overview' | 'company' | 'marketing' | 'athletes' | 'contact') => {
    setEditSection(section);
    setEditDrawerOpen(true);
  };

  const handleSave = async (updatedData: any) => {
    try {
      await userService.updateProfile(profile.id, updatedData);
      await refreshUserData();
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <ProfileCompletionPrompt />
      
      {/* Admin Section */}
      {isAdmin && <AdminProfileSection />}
      
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="text-lg">
                  {brand.company_name?.charAt(0)?.toUpperCase() || profile.display_name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <Button
                variant="outline"
                size="icon"
                className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full shadow-md bg-background"
                onClick={() => navigate('/settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {brand.company_name || profile.display_name}
                  </h1>
                  <p className="text-muted-foreground">
                    {brand.industry || 'Business'} • {brand.company_size || 'Company'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {profile.is_verified && (
                    <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                      <UserCheck className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                  <Badge variant="outline">
                    Brand
                  </Badge>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                {profile.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {profile.location}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Joined {new Date(profile.created_at).toLocaleDateString()}
                </div>
                {brand.website && (
                  <div className="flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    <a href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`} 
                       target="_blank" rel="noopener noreferrer" 
                       className="text-primary hover:underline">
                      {brand.website}
                    </a>
                  </div>
                )}
              </div>

              {(brand.description || profile.bio) && (
                <p className="mt-4 text-foreground leading-relaxed">
                  {brand.description || profile.bio}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabbed Profile Content */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid grid-cols-3 md:grid-cols-7 h-auto w-full">
          <TabsTrigger value="overview" className="whitespace-nowrap text-xs md:text-sm">Overview</TabsTrigger>
          <TabsTrigger value="company" className="whitespace-nowrap text-xs md:text-sm">Company</TabsTrigger>
          <TabsTrigger value="marketing" className="whitespace-nowrap text-xs md:text-sm">Marketing</TabsTrigger>
          <TabsTrigger value="athletes" className="whitespace-nowrap text-xs md:text-sm">Athletes</TabsTrigger>
          <TabsTrigger value="contact" className="whitespace-nowrap text-xs md:text-sm">Contact</TabsTrigger>
          <TabsTrigger value="organization" className="whitespace-nowrap text-xs md:text-sm">Organization</TabsTrigger>
          <TabsTrigger value="social" className="whitespace-nowrap text-xs md:text-sm">Social Media</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Brand Overview
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('overview')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Display Name</span>
                <span className="font-medium">{profile.display_name || 'Not provided'}</span>
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-start">
                <span className="text-sm text-muted-foreground sm:flex-shrink-0">Bio</span>
                <span className="font-medium sm:text-right sm:max-w-xs sm:truncate">{profile.bio || 'Not provided'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Location</span>
                <span className="font-medium">{profile.location || 'Not provided'}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="company" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Company Details
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('company')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Company Name</span>
                <span className="font-medium">{brand.company_name || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Industry</span>
                <span className="font-medium">{brand.industry || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Company Size</span>
                <span className="font-medium">{brand.company_size || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Website</span>
                {brand.website ? (
                  <a href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`} 
                     target="_blank" rel="noopener noreferrer" 
                     className="text-primary hover:underline flex items-center gap-1">
                    {brand.website}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="font-medium">Not specified</span>
                )}
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Description</span>
                <span className="font-medium text-right max-w-xs">{brand.description || 'Not provided'}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="marketing" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Marketing Focus
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('marketing')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Budget Range</span>
                <span className="font-medium">{brand.budget_range || 'Not disclosed'}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Marketing Objectives</span>
                <span className="font-medium text-right max-w-xs">{brand.marketing_objectives || 'Not specified'}</span>
              </div>
              {brand.target_demographics && Array.isArray(brand.target_demographics) && brand.target_demographics.length > 0 && (
                <div>
                  <span className="text-sm text-muted-foreground block mb-2">Target Demographics</span>
                  <div className="flex flex-wrap gap-2">
                    {brand.target_demographics.map((demo: any, index: number) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {typeof demo === 'string' ? demo : demo.name || 'Demographic'}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {brand.campaign_objectives && Array.isArray(brand.campaign_objectives) && brand.campaign_objectives.length > 0 && (
                <div>
                  <span className="text-sm text-muted-foreground block mb-2">Campaign Objectives</span>
                  <div className="flex flex-wrap gap-2">
                    {brand.campaign_objectives.map((objective: any, index: number) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {typeof objective === 'string' ? objective : objective.name || 'Objective'}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="athletes" className="space-y-4">
          <AthleteEngagementGrid 
            athletes={athletes} 
            onViewHistory={handleViewHistory}
          />
        </TabsContent>

        <TabsContent value="contact" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Contact Information
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('contact')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="font-medium">{profile.email}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Contact Person</span>
                <span className="font-medium">{brand.contact_person_name || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Contact Title</span>
                <span className="font-medium">{brand.contact_person_title || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Phone</span>
                <span className="font-medium">{brand.contact_phone || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Account Type</span>
                <Badge variant="outline">{profile.account_type}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Profile Status</span>
                <Badge variant={profile.setup_completed ? "default" : "secondary"}>
                  {profile.setup_completed ? 'Complete' : 'Incomplete'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="organization" className="space-y-4">
          <BrandOrganizationTab />
        </TabsContent>

        <TabsContent value="social" className="space-y-4">
          <SocialMediaLinksTab />
        </TabsContent>

      </Tabs>

      <BrandProfileEditDrawer
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        section={editSection}
        data={brandData}
        onSave={handleSave}
      />
    </div>
  );
};

export default BrandProfile;